import { Tag, TagCloseButton, TagLabel, TagLeftIcon, Wrap, WrapItem } from '@chakra-ui/react'
import { Library, ListFilter } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useCommands } from '../hooks/commands/useCommands'
import { describeCommand, describeCommandArgs } from '../core/parsers/commandSelection'
import { ParsedCommand } from '../types/commandTypes'

interface ScopeBadgeProps {
  icon?: React.ElementType
  label: string
  size?: 'sm' | 'md'
  onRemove?: () => void
}

export const ScopeBadge: React.FC<ScopeBadgeProps> = ({ icon, label, size = 'sm', onRemove }) => (
  <Tag size={size} variant='subtle' colorScheme='gray' borderRadius='full'>
    {icon && <TagLeftIcon as={icon} />}
    <TagLabel>{label}</TagLabel>
    {onRemove && <TagCloseButton onClick={onRemove} />}
  </Tag>
)

interface AppliedCommandTagsProps {
  commands?: ParsedCommand[]
}

/** Read-only chips for commands not handled by AnswerScopeTags. Renders nothing when empty. */
export const AppliedCommandTags: React.FC<AppliedCommandTagsProps> = ({ commands }) => {
  const { commands: descriptors } = useCommands()

  const rest = (commands ?? []).filter(
    command => command.name !== 'knowledge' && command.name !== 'org',
  )

  if (rest.length === 0) return null

  return (
    <Wrap spacing={2} mt={1}>
      {rest.map(command => (
        <WrapItem key={command.name}>
          <Tag size='sm' variant='subtle' colorScheme='brand' borderRadius='full'>
            <TagLabel>{describeCommand(command, descriptors)}</TagLabel>
          </Tag>
        </WrapItem>
      ))}
    </Wrap>
  )
}

interface AnswerScopeTagsProps {
  commands?: ParsedCommand[]
}

/**
 * Flag-style chips echoing the menu directives a reply ran under — the
 * HR-knowledge toggle ("HR knowledge off") and the `org` filter ("Filtered to:
 * KBC, SLOB"). Both share one gray, subtle, icon style and sit next to the
 * sources bubble in the answer's action row. Renders nothing on default turns.
 */
export const AnswerScopeTags: React.FC<AnswerScopeTagsProps> = ({ commands }) => {
  const { t } = useTranslation()
  const { commands: descriptors } = useCommands()

  const list = commands ?? []
  const knowledgeOff = list.some(
    command => command.name === 'knowledge' && command.args.includes('off'),
  )
  const filter = list.find(command => command.name === 'org' && command.args.length > 0)

  if (!knowledgeOff && !filter) return null

  return (
    <>
      {knowledgeOff && <ScopeBadge icon={Library} label={t('chat.hrKnowledgeOff')} />}
      {filter && (
        <ScopeBadge
          icon={ListFilter}
          label={`${t('chat.filteredTo')}: ${describeCommandArgs(filter, descriptors)}`}
        />
      )}
    </>
  )
}
